import React, { useState, useEffect } from 'react';
import { apiConfig, buildApiUrl } from '../config/api';

interface ContactSummary {
  totalPeople: number;
  emailCompleteness: number;
  phoneCompleteness: number;
  addressCompleteness: number;
  peopleWithEmail: number;
  peopleWithPhone: number;
  peopleWithAddress: number;
}

interface ContactBreakdown {
  contactType: string;
  count: number;
  percentage: number;
}

interface EmailDomain {
  domain: string;
  count: number;
  percentage: number;
}

interface ContactData {
  summary: ContactSummary;
  contactBreakdown: ContactBreakdown[];
  emailDomains: EmailDomain[];
}

const ContactCompletenessReport: React.FC = () => {
  const [data, setData] = useState<ContactData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchContactData();
  }, []);

  const fetchContactData = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(apiConfig.endpoints.analytics.contactCompleteness));
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError('Failed to fetch contact completeness data');
      }
    } catch (err) {
      setError('Error fetching contact completeness data');
      console.error('Contact data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button 
          onClick={fetchContactData}
          className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return <div>No contact completeness data available</div>;
  }

  const getCompletenessColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    if (percentage >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getCompletenessBarColor = (percentage: number) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-yellow-500';
    if (percentage >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">📱 Contact Completeness Report</h1>
        <p className="text-gray-600 mb-6">
          Data quality insights showing contact information completeness across your member database.
        </p>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800">Total People</h3>
            <p className="text-2xl font-bold text-blue-900">{data.summary.totalPeople.toLocaleString()}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-green-800">With Email</h3>
            <p className="text-2xl font-bold text-green-900">{data.summary.peopleWithEmail.toLocaleString()}</p>
            <p className="text-sm text-green-700">{data.summary.emailCompleteness}% complete</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-purple-800">With Phone</h3>
            <p className="text-2xl font-bold text-purple-900">{data.summary.peopleWithPhone.toLocaleString()}</p>
            <p className="text-sm text-purple-700">{data.summary.phoneCompleteness}% complete</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-orange-800">With Address</h3>
            <p className="text-2xl font-bold text-orange-900">{data.summary.peopleWithAddress.toLocaleString()}</p>
            <p className="text-sm text-orange-700">{data.summary.addressCompleteness}% complete</p>
          </div>
        </div>

        {/* Completeness Progress Bars */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📊 Data Completeness Overview</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Email Addresses</span>
                <span className={`text-sm font-semibold ${getCompletenessColor(data.summary.emailCompleteness)}`}>
                  {data.summary.emailCompleteness}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${getCompletenessBarColor(data.summary.emailCompleteness)}`}
                  style={{ width: `${data.summary.emailCompleteness}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Phone Numbers</span>
                <span className={`text-sm font-semibold ${getCompletenessColor(data.summary.phoneCompleteness)}`}>
                  {data.summary.phoneCompleteness}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${getCompletenessBarColor(data.summary.phoneCompleteness)}`}
                  style={{ width: `${data.summary.phoneCompleteness}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Addresses</span>
                <span className={`text-sm font-semibold ${getCompletenessColor(data.summary.addressCompleteness)}`}>
                  {data.summary.addressCompleteness}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${getCompletenessBarColor(data.summary.addressCompleteness)}`}
                  style={{ width: `${data.summary.addressCompleteness}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Method Breakdown */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📋 Contact Method Breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.contactBreakdown.map((item) => (
              <div
                key={item.contactType}
                className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.contactType}</h3>
                <p className="text-2xl font-bold text-blue-600 mb-1">{item.count.toLocaleString()}</p>
                <p className="text-sm text-gray-600">{item.percentage}% of total</p>
              </div>
            ))}
          </div>
        </div>

        {/* Email Domains Analysis */}
        {data.emailDomains.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">📧 Email Domain Analysis</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Domain
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Percentage of Emails
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Provider Type
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.emailDomains.map((domain) => {
                    const getProviderType = (domainName: string) => {
                      if (['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'].includes(domainName)) {
                        return { type: 'Personal', color: 'bg-blue-100 text-blue-800' };
                      }
                      if (domainName.includes('.edu')) {
                        return { type: 'Educational', color: 'bg-purple-100 text-purple-800' };
                      }
                      if (domainName.includes('.gov') || domainName.includes('.mil')) {
                        return { type: 'Government', color: 'bg-green-100 text-green-800' };
                      }
                      return { type: 'Business/Other', color: 'bg-gray-100 text-gray-800' };
                    };

                    const providerInfo = getProviderType(domain.domain);

                    return (
                      <tr key={domain.domain} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {domain.domain}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {domain.count.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {domain.percentage}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${providerInfo.color}`}>
                            {providerInfo.type}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Data Quality Recommendations */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-yellow-900 mb-3">💡 Data Quality Recommendations</h2>
          <div className="space-y-2 text-sm text-yellow-800">
            {data.summary.emailCompleteness < 80 && (
              <p>• <strong>Email Collection:</strong> Focus on gathering email addresses - currently at {data.summary.emailCompleteness}%</p>
            )}
            {data.summary.phoneCompleteness < 70 && (
              <p>• <strong>Phone Numbers:</strong> Improve phone number collection for emergency contacts</p>
            )}
            {data.summary.addressCompleteness < 60 && (
              <p>• <strong>Address Data:</strong> Collect addresses for geographic insights and mail campaigns</p>
            )}
            <p>• <strong>Data Verification:</strong> Regularly verify and update contact information</p>
            <p>• <strong>Multi-Channel:</strong> Encourage members to provide multiple contact methods</p>
            {data.contactBreakdown.find(item => item.contactType === 'No Contact Info') && (
              <p>• <strong>Priority:</strong> {data.contactBreakdown.find(item => item.contactType === 'No Contact Info')?.count} people have no contact information</p>
            )}
            <p>• <strong>Small Group Planning:</strong> Complete contact data enables better geographic analysis for new group locations</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactCompletenessReport;
